import type { SearchProps } from '@iheartjane/algolia';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { composeFilters } from '@iheartjane/menu';
import type { Nullable, QualifyingStatusEnum } from '@iheartjane/types';

import type { AlgoliaProduct } from '@jane/search/types';
/* eslint-disable-next-line no-restricted-imports */
import type {
  StoreSpecial,
  BrandSpecial as tBrandSpecial,
} from '@jane/shared/models';
import type {
  BrandSpecial,
  StoreSpecial as ZStoreSpecial,
} from '@jane/shared/types';

import type { MenuCategory } from '../../types';
import {
  buildRootTypeFilter,
  buildSpecialFilter,
  buildStoreFilter,
} from '../../util';

interface SearchOptionsArgs {
  brandSpecial?: Nullable<BrandSpecial | tBrandSpecial>;
  categories?: Array<MenuCategory | undefined>;
  /**
   * Adds a static filter for custom row categories. This will be used in place of the root type filter
   * created from a selected `category`.
   */
  customRowFilter?: string;
  /**
   * Optionally order search results to promote certain products, usually a brand's products.
   * This is mostly important when searching and displaying menu products directly, rather than when obtaining facets for filters.
   * @see https://www.algolia.com/doc/guides/managing-results/rules/merchandising-and-promoting/in-depth/optional-filters/
   */
  optionalFilters?: string;
  /**
   * The relevant QualifyingStatus if a store or brand special is in scope.
   */
  qualifyingStatus?: Nullable<QualifyingStatusEnum>;
  storeId: number;
  storeSpecial?: Nullable<StoreSpecial | ZStoreSpecial | V3StoreSpecial>;
}

export const searchOptions = ({
  brandSpecial,
  categories,
  customRowFilter,
  optionalFilters,
  qualifyingStatus,
  storeId,
  storeSpecial,
}: SearchOptionsArgs): Pick<
  SearchProps<AlgoliaProduct>,
  'options' | 'staticFilters'
> => ({
  options: {
    optionalFilters,
  },
  staticFilters: composeFilters(
    buildStoreFilter(storeId),
    customRowFilter,
    buildSpecialFilter({
      special: storeSpecial,
      qualifyingStatus,
    }),
    buildSpecialFilter({
      isBrandSpecial: !!brandSpecial,
      special: brandSpecial,
      qualifyingStatus,
    }),
    buildRootTypeFilter(categories)
  ),
});
